import {
  LIST_ALERTS_MANAGEMENT,
  OrderByInput,
  useAlertsManagementListFilter,
  useAlertsManagementListOrder,
  useCurrentWorkspace,
  useSetAlertsManagementListOrder,
} from '@gripp/shared-logic';
import { ColumnDef } from '@tanstack/react-table';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InfiniteDataGrid, SelectionStatus } from '../infiniteDataGrid';
import { AlertsManagementTableData, useColumns } from './useColumns';

export type SelectedAlertRow = AlertsManagementTableData;

export type AlertsManagementListProps = {
  maxHeight?: number;
  paddingBottom?: number;
  preserveScrollKey?: string;
  onRowClick: (alert: AlertsManagementTableData) => void;
  /** Legacy top-bar buttons (optional). */
  editAlertMenu?: (props: {
    alertId: string;
    label: string;
  }) => React.ReactElement;
  alertsStatusButton?: (props: {
    alertIds: string[];
    isEnableAction: boolean;
    onStatusChange: () => void;
  }) => React.ReactElement;
  /** Crop Circle side-panel wiring. */
  onRowsSelectedChange?: (rows: SelectedAlertRow[]) => void;
  selectedRowIds?: string[];
  selectionStatus?: SelectionStatus;
  onSelectionStatusChange?: (status: SelectionStatus) => void;
};

export const AlertsManagementList = ({
  maxHeight,
  paddingBottom,
  preserveScrollKey,
  editAlertMenu,
  onRowClick,
  alertsStatusButton,
  onRowsSelectedChange,
  selectedRowIds,
  selectionStatus,
  onSelectionStatusChange,
}: AlertsManagementListProps) => {
  const { t } = useTranslation();

  const EditAlertMenu = editAlertMenu;

  const { data } = useAlertsManagementListFilter();
  const { data: orderData } = useAlertsManagementListOrder();
  const { mutateAsync: setOrder } = useSetAlertsManagementListOrder();
  const workspace = useCurrentWorkspace(true);

  const setOrderData = useCallback(
    (order: OrderByInput) => {
      setOrder(order);
    },
    [setOrder]
  );

  const [filter, setFilter] = useState<any>(undefined);
  const [columns, setColumns] = useState<string[]>([]);
  // Legacy internal selection state — only used when the legacy button
  // render props (`editAlertMenu` / `alertsStatusButton`) are provided.
  const [legacyAlertsSelected, setLegacyAlertsSelected] = useState<
    AlertsManagementTableData[]
  >([]);

  const showLegacyAlertMenu = useMemo(
    () => legacyAlertsSelected.length === 1,
    [legacyAlertsSelected]
  );
  const showLegacyStatusButton = useMemo(
    () => legacyAlertsSelected.length > 0,
    [legacyAlertsSelected]
  );
  const legacyIsEnableAction = useMemo(
    () => legacyAlertsSelected.every((alert) => alert.disabled),
    [legacyAlertsSelected]
  );

  const updateRowsSelected = (rows: AlertsManagementTableData[]) => {
    if (onRowsSelectedChange) {
      onRowsSelectedChange(rows);
    } else {
      setLegacyAlertsSelected(rows);
    }
  };

  useEffect(() => {
    if (!data || workspace === undefined) return;
    setColumns(data.columns || []);

    const f = { ...data };
    f.workspaceId = workspace?.id;
    if (f.keyword === '') delete f.keyword;
    if (f.status === '') delete f.status;
    delete f.columns;
    setFilter(f);
  }, [data, workspace]);

  const filterWithColumns = useMemo(() => {
    return filter ? { ...filter, columns } : undefined;
  }, [filter, columns]);

  const columnsWithFilter = useColumns(filterWithColumns) as ColumnDef<
    any,
    any
  >[];

  const selectedIdsSet = useMemo(
    () => new Set(selectedRowIds ?? []),
    [selectedRowIds]
  );
  const isRowSelected = useCallback(
    (row: { id?: string }) => (row?.id ? selectedIdsSet.has(row.id) : false),
    [selectedIdsSet]
  );

  const hasLegacyHeader = !!alertsStatusButton || !!EditAlertMenu;

  const RenderLegacyHeader = () => {
    return (
      <>
        {showLegacyStatusButton &&
          alertsStatusButton &&
          (() => {
            const alertIds = legacyAlertsSelected.map((alert) => alert.id);
            return alertsStatusButton({
              alertIds,
              isEnableAction: legacyIsEnableAction,
              onStatusChange: () => setLegacyAlertsSelected([]),
            });
          })()}

        {showLegacyAlertMenu &&
          legacyAlertsSelected.length === 1 &&
          EditAlertMenu && (
            <EditAlertMenu
              alertId={legacyAlertsSelected[0].id}
              label={t('alertsManagement.menu.editAlert')}
            />
          )}
      </>
    );
  };

  return !filter ? null : (
    <InfiniteDataGrid
      modelName="workflow"
      query={LIST_ALERTS_MANAGEMENT}
      orderBy={orderData}
      filter={filter}
      pageSize={50}
      columns={columnsWithFilter}
      listMaxHeight={maxHeight as number}
      paddingBottom={paddingBottom as number}
      textEmpty={t('alertsManagement.common.textEmpty')}
      dataPrefix="workflow.listAlerts"
      enableScrollRefresh={true}
      preserveScrollKey={preserveScrollKey}
      showScrollIndicator={true}
      exportFileName="alerts"
      updateRowsSelected={updateRowsSelected}
      isRowSelected={onRowsSelectedChange ? isRowSelected : undefined}
      selectionStatus={selectionStatus}
      onSelectionStatusChange={onSelectionStatusChange}
      headerContent={hasLegacyHeader ? <RenderLegacyHeader /> : undefined}
      onOrderByChange={setOrderData}
      onRowClick={(r) => onRowClick(r)}
    />
  );
};
